/**
 * UserCmd.js
 * Classes to encapsulate user actions in BMaps
 *
 * The following concepts are defined:
 * UserCmd: is a possible kind of action, but without specific arguments (analogous to a class)
 * UserAction: is an the "instantiation" of the UserCmd, with arguments
 * UserActions is an object containing functions for to run all the UserCmds. (dynamically built)
 *
 * Normal use is to import UserActions from UserAction.js and call the functions on it.
 *
 * Action Queue:
 * Actions are put into a queue to hopefully allow for command undo/redo.
 * EventBroker "zapAll" is subscribed, so "ActionLog" and "UndoQueue" can be cleared on zap,
 * along with every snapshot the undoable commands have recorded (see ./undoable.js).
 *
 * Undo:
 * A command is undoable in one of two ways. The older is an `undoFn` option, which receives the
 * command's own arguments -- enough when the inverse can be computed from them (no command on
 * main uses it now). The other is the capture / apply / restore triple: `capture(...args)`
 * remembers what is about to change, `apply(...args)` is the command body, and
 * `restore(snapshot)` puts it back. ./undoable.js composes those into the doFn/undoFn pair and
 * owns where the snapshot lives between do and undo.
 */

import { EventBroker } from '../eventbroker';
import { undoable, resetAll } from './undoable';

// A UserCmd is a potential user action.
// It contains the logic, but not the arguments.
export class UserCmd {
    constructor(name, doFn, options={}) {
        this.name = name;
        this.redoFn = options.redoFn;
        // "Running me changes nothing" -- a pure read, or undo/redo itself. Distinct from having
        // no undoFn, which means "I changed something you cannot get back". See Enqueue.
        this.noEffect = options.noEffect;

        const { capture, restore, undoFn } = options;
        if (capture || restore) {
            if (undoFn) {
                throw new TypeError(`UserCmd ${name}: declare capture/restore or undoFn, not both.`);
            }
            const composed = undoable({
                name, capture, apply: doFn, restore,
            });
            // Kept addressable on the command, not only inside the composed closures, so the
            // snapshot's home can move into the queued UserAction without touching a command.
            this.capture = capture;
            this.apply = doFn;
            this.restore = restore;
            this.doFn = composed.doFn;
            this.undoFn = composed.undoFn;
        } else {
            this.doFn = doFn;
            this.undoFn = undoFn;
        }
    }

    do(...args) {
        return UserAction.do(this, ...args);
    }
}

// A UserAction is something the user has done.
// It is a UserCmd (a potential action) plus arguments.
// A UserAction can theoretically be undone and redone.
export class UserAction {
    static do(cmd, ...args) {
        return new UserAction(cmd, ...args).do();
    }

    static undo() {
        if (UserAction.canUndo()) {
            UserAction.PrevAction.undo();
        }
    }

    static redo() {
        if (UserAction.canRedo()) {
            UserAction.NextAction.redo();
        }
    }

    static canUndo() {
        return UserAction.PrevAction && UserAction.PrevAction.cmd.undoFn;
    }

    static canRedo() {
        return UserAction.NextAction;
    }

    constructor(cmd, ...args) {
        this.cmd = cmd;
        this.args = args;
    }

    do() {
        const cmd = this.cmd;
        // Every action passes here, from a menu or from Gibbs, so this is where the Workflow
        // pane's macro recorder hears them (gibbs/workflow/macroCapture.js). It hears only the
        // outermost one, once it has succeeded: an action another runs is part of that one, and
        // replaying both would run it twice. An asynchronous action counts as running until it
        // settles, so an action started meanwhile counts as inside it too.
        const isOutermost = UserAction.running === 0;
        if (isOutermost) {
            UserAction.outermostAction = { name: cmd.name, returned: false };
        } else if (UserAction.outermostAction?.returned) {
            // Started while the outermost action awaits something: one it runs, or one the user
            // started meanwhile. Nothing here tells them apart, so the recorder says so.
            EventBroker.publish('actionNotRecorded', {
                userActionName: cmd.name, during: UserAction.outermostAction.name,
            });
        }
        UserAction.running += 1;
        const settled = (succeeded) => {
            UserAction.running -= 1;
            if (isOutermost) UserAction.outermostAction = null;
            if (succeeded && isOutermost) {
                EventBroker.publish('actionCompleted', { userActionName: cmd.name, params: this.args });
            }
        };
        let result;
        try {
            result = cmd.doFn(...this.args);
        } catch (err) {
            settled(false);
            throw err;
        }
        if (isOutermost) UserAction.outermostAction.returned = true;
        UserAction.Enqueue(this);
        if (typeof result?.then === 'function') {
            result.then(() => settled(true), () => settled(false));
        } else {
            settled(true);
        }
        return result;
    }

    undo() {
        const cmd = this.cmd;
        if (cmd.undoFn) {
            UserAction.UndoIndex--;
            return cmd.undoFn(...this.args);
        } else {
            console.warn('Attempted to undo un-undoable action.');
            return undefined;
        }
    }

    redo() {
        const cmd = this.cmd;
        // Not exactly sure how redo will be used, but this allows a custom "redo" function,
        // or fallback to "do" function
        const verb = cmd.redoFn ? cmd.redoFn : cmd.doFn;
        if (UserAction.UndoQueue[UserAction.UndoIndex] === this) {
            UserAction.UndoIndex++;
            try {
                verb(...this.args);
            } catch (error) {
                // A redo that throws did not happen: leave it redoable, and keep the index in
                // step with the undoable seam, which records nothing for a call that threw.
                UserAction.UndoIndex--;
                throw error;
            }
        } else {
            console.warn('Attempted to redo the wrong action.');
        }
    }

    // Action queue managment
    static setupUndoQueue() {
        if (!UserAction.undoQueue) {
            UserAction.undoQueue = [];
            UserAction.undoIndex = 0;
        }
    }

    static get ActionLog() {
        if (!UserAction.actionLog) {
            UserAction.actionLog = [];
            UserAction.zapHandler = EventBroker.subscribe('zapAll', () => UserAction.ResetQueues());
        }
        return UserAction.actionLog;
    }

    static get UndoQueue() {
        UserAction.setupUndoQueue();
        return UserAction.undoQueue;
    }

    static get UndoIndex() {
        UserAction.setupUndoQueue();
        return UserAction.undoIndex;
    }

    static set UndoIndex(index) {
        UserAction.setupUndoQueue();
        UserAction.undoIndex = index;
    }

    static get PrevAction() {
        UserAction.setupUndoQueue();
        return UserAction.UndoQueue[UserAction.UndoIndex-1];
    }

    static get NextAction() {
        UserAction.setupUndoQueue();
        return UserAction.UndoQueue[UserAction.UndoIndex];
    }

    static Enqueue(action) {
        UserAction.ActionLog.push(action);
        // Two separate questions, and conflating them is what the old `skipQueue` did:
        //   1. Did this command change anything?  If not, leave the queue untouched.
        //   2. Can it be undone?  Only then is it worth recording.
        // A command that changes state but has no undoFn answers yes/no -- it must still discard
        // the redo branch, because whatever sits ahead of UndoIndex describes a state that no
        // longer exists. Losing that was a regression in the first cut of BP2-2723.
        if (!action.cmd.noEffect) {
            UserAction.UndoQueue.splice(UserAction.UndoIndex);
            if (action.cmd.undoFn) {
                UserAction.UndoQueue.push(action);
                UserAction.UndoIndex++;
            }
        }
    }

    static ResetQueues() {
        UserAction.actionLog = [];
        UserAction.undoQueue = [];
        UserAction.undoIndex = 0;
        resetAll();
    }

    static DumpQueues() {
        const dumpOneQueue = (q, active) => q.map((a, i) => `${active===i?'*':''}${a.cmd.name}`).join('\n');
        console.log(`Action log: ${dumpOneQueue(UserAction.ActionLog)}`);
        console.log(`Undo queue: ${dumpOneQueue(UserAction.UndoQueue, UserAction.UndoIndex)}`);
    }
}
// How many actions are running, an asynchronous one until it settles (see do()).
UserAction.running = 0;
// The outermost running action's name, and whether its doFn has returned (see do()).
UserAction.outermostAction = null;

// CmdRegistry is an object with functions that will execute
// every possible UserCmd
export const UserActions = {

};

export const CustomActions = {
    Compound: [],
    // CompoundGroup: [],
    // CompoundView: [],
    // Fragment: [],
    // FragmentGroup: [],
    // FragmentView: [],
    // ProteinView: [],
    // HotspotGroup: [],
};

export function AddUserActions(cmdObj, childKey) {
    const host = childKey ? UserActions[childKey] : UserActions;
    for (const [cmdName, cmdFn] of Object.entries(cmdObj)) {
        if (host[cmdName]) console.warn(`${cmdName} is already defined as a user action${childKey ? `on ${childKey}` : ''}. Overriding...`);
        host[cmdName] = (...args) => UserAction.do(cmdFn, ...args);
    }
}

export function AddCustomAction(type, actions) {
    for (const action of [].concat(actions)) {
        CustomActions[type].push(action);
    }
}

// noEffect, not merely "no undoFn": these two WALK the queue, so they must not disturb it.
// Without it, Undo would splice away the very redo branch it had just created.
const UndoActions = {
    Undo: new UserCmd('Undo', UserAction.undo, { noEffect: true }),
    Redo: new UserCmd('Redo', UserAction.redo, { noEffect: true }),
};

AddUserActions(UndoActions);
